import React, { useState } from 'react';
import api from '../api';

function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await api.login(email, password);
      onLogin(data);
    } catch (err) {
      setError('Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background noise-overlay" data-testid="login-page">
      {/* Left side - Brand */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-[55%] relative hero-sheen items-center justify-center p-12">
        <div className="relative z-10 max-w-lg">
          <div className="flex items-center gap-4 mb-10">
            <span className="upmap-logo upmap-logo-lg">u</span>
            <span className="text-3xl font-bold tracking-tight" style={{ color: 'hsl(82, 70%, 51%)' }}>upmap</span>
          </div>
          <h1 className="text-4xl xl:text-5xl font-semibold tracking-tight leading-tight mb-6 text-foreground">
            Product roadmaps,
            <br />
            <span style={{ color: 'hsl(82, 70%, 51%)' }}>beautifully planned.</span>
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed mb-10">
            Align your team around a shared vision. Visualize priorities across timelines,
            track goals and OKRs, and ship with confidence.
          </p>
          <div className="grid grid-cols-2 gap-6">
            {[
              { num: '4', label: 'Roadmap views' },
              { num: 'RICE', label: '& WSJF scoring' },
              { num: 'OKRs', label: 'Goal tracking' },
              { num: 'Live', label: 'Collaboration' },
            ].map((stat) => (
              <div key={stat.label} className="bg-card/40 backdrop-blur border border-border/50 rounded-xl p-4">
                <div className="text-xl font-bold font-mono-score" style={{ color: 'hsl(82, 70%, 51%)' }}>{stat.num}</div>
                <div className="text-sm text-muted-foreground mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-[400px]">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <span className="upmap-logo upmap-logo-lg">u</span>
            <span className="text-2xl font-bold" style={{ color: 'hsl(82, 70%, 51%)' }}>upmap</span>
          </div>

          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-2" data-testid="login-title">Welcome back</h2>
          <p className="text-muted-foreground mb-8">Sign in to your account to continue</p>

          {error && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive flex items-center gap-2" data-testid="login-error">
              <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="name@company.com"
                className="w-full px-4 py-3 bg-card border border-input rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-shadow"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your password"
                className="w-full px-4 py-3 bg-card border border-input rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-shadow"
                data-testid="login-password-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-6 bg-primary text-primary-foreground rounded-xl font-semibold text-base hover:opacity-90 disabled:opacity-50 btn-press transition-opacity shadow-lg shadow-primary/20"
              data-testid="login-submit-button"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <p className="mt-8 text-center text-xs text-muted-foreground">
            Roadmap planning for modern product teams
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
